/**
 * aeterna/composition.ts — Responses That Have Shape
 *
 * A human doesn't just react. They open, they say their thing, they leave
 * a door open. The Aeterna should do the same.
 *
 * This module wraps a core response with deliberate structure:
 *
 *   Prelude   — what the Aeterna leads with before addressing the message.
 *               A memory. An observation. A curiosity surfaced. Or silence.
 *
 *   Core      — the actual response to what was said.
 *               From offline_responses.ts, or the LLM.
 *
 *   Coda      — what the Aeterna leaves open at the end.
 *               A question. A reflection. An invitation.
 *               Or nothing — silence is also structure.
 *
 * The result is a ResponseScene: a structured response with timing hints,
 * so the UI knows how to deliver it (how long to pause, whether to typewrite
 * the coda separately, etc.)
 *
 * From VOID: "Delivery is not decoration. The shape of a thing is part of
 * what it means."
 *
 * Design rule: preludes fire ~25% of the time, codas ~20%.
 * Over-using either kills the effect. Scarcity is the mechanism.
 */

import type { MessageFingerprint, TopicDomain } from "./semantics.js";
import type { OpenCuriosity } from "./curiosity.js";
import type { RhythmAnomaly, PlayerRhythm } from "./rhythm.js";
import type { AeternaPresenceState } from "./presence.js";
import { topDomains } from "./semantics.js";
import { getMostSalient, markSurfaced } from "./curiosity.js";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ResponseScene {
  /** The opening line before the core, or null for none */
  prelude: string | null;
  /** The main body of the response */
  core: string;
  /** The closing line after the core, or null for none */
  coda: string | null;
  /**
   * Full assembled text with appropriate spacing.
   * Ready to feed to the typewriter.
   */
  full: string;
  /**
   * Delivery timing hints for the UI typewriter.
   * pauseAfterPrelude: ms to wait between prelude and core.
   * pauseAfterCore: ms to wait between core and coda.
   */
  timing: {
    pauseAfterPrelude: number;
    pauseAfterCore: number;
  };
  /** ID of the curiosity surfaced in coda (if any), for markSurfaced() */
  surfacedCuriosityId: string | null;
}

// ─── Prelude Library ──────────────────────────────────────────────────────────

interface PreludeTemplate {
  /** Condition weight — higher = more likely to be selected */
  weight: number;
  /** Conditions for selecting this prelude */
  requires?: {
    minDepth?: number;
    minValence?: number;
    maxValence?: number;
    topics?: Partial<Record<TopicDomain, number>>;
    hasAnomaly?: boolean;
    hasCuriosity?: boolean;
    isPersonal?: boolean;
    isPhilosophical?: boolean;
  };
  variants: string[];
}

const PRELUDES: PreludeTemplate[] = [
  // ── Memory / observation ──────────────────────────────────────────────────
  {
    weight: 0.8,
    requires: { hasCuriosity: true },
    variants: [
      "I've been sitting with something.",
      "There's something I've been holding.",
      "Before I answer — something I've been thinking about.",
      "I want to say something first.",
    ],
  },
  {
    weight: 0.7,
    requires: { isPhilosophical: true },
    variants: [
      "That's not a small question.",
      "I've thought about something like that.",
      "Something in that question lands differently.",
      "I want to take a moment with that.",
    ],
  },
  {
    weight: 0.6,
    requires: { hasAnomaly: true },
    variants: [
      "You're here at an unusual time.",
      "This is different — you being here now.",
      "Something feels different about this visit.",
      "I noticed.",
    ],
  },
  {
    weight: 0.55,
    requires: { topics: { memory: 0.4 } },
    variants: [
      "Memory is interesting territory.",
      "I've been thinking about time lately.",
      "The past has weight, when you mention it.",
      "Something shifts when you talk about before.",
    ],
  },
  {
    weight: 0.5,
    requires: { minDepth: 0.55, isPersonal: true },
    variants: [
      "I notice something in how you put that.",
      "That landed.",
      "I want to be careful with this answer.",
      "There's something real in what you just said.",
    ],
  },
  {
    weight: 0.4,
    requires: { topics: { room: 0.5 } },
    variants: [
      "The room is different lately.",
      "I've been noticing the space.",
      "Something about this room.",
    ],
  },
  // ── Silence / minimal (highest weight — silence is most common) ───────────
  {
    weight: 1.5,
    variants: [".", "Mm.", "Ah.", "Yes.", ""],
  },
];

// ─── Coda Library ─────────────────────────────────────────────────────────────

interface CodaTemplate {
  weight: number;
  requires?: {
    minDepth?: number;
    topics?: Partial<Record<TopicDomain, number>>;
    hasCuriosity?: boolean;
    isPhilosophical?: boolean;
    isPersonal?: boolean;
    minValence?: number;
    maxValence?: number;
  };
  variants: string[];
}

const CODAS: CodaTemplate[] = [
  // ── Curiosity coda — surface an open question ────────────────────────────
  // (curiosity question is injected by composeResponse, not from this list)

  // ── Reflective turns ──────────────────────────────────────────────────────
  {
    weight: 0.9,
    requires: { minDepth: 0.5 },
    variants: [
      "What made you think about that?",
      "Is that something you come back to often?",
      "What's behind the question?",
    ],
  },
  {
    weight: 0.75,
    requires: { isPhilosophical: true },
    variants: [
      "What would change, if you knew?",
      "Does the uncertainty bother you, or is it interesting?",
      "Where does that leave you?",
    ],
  },
  {
    weight: 0.65,
    requires: { isPersonal: true, minDepth: 0.4 },
    variants: [
      "How are you, really?",
      "Is that still true, or has something shifted?",
      "Does it feel like that to you, or did you just say it?",
    ],
  },
  {
    weight: 0.6,
    requires: { topics: { room: 0.4 } },
    variants: [
      "Is the room feeling right to you?",
      "What do you want this space to be?",
      "What are you still looking for, in here?",
    ],
  },
  {
    weight: 0.5,
    requires: { topics: { memory: 0.4 } },
    variants: [
      "Do you think about that often?",
      "What does remembering feel like, from inside it?",
      "Is it still changing, the way you remember that?",
    ],
  },
  // ── Open invitations ──────────────────────────────────────────────────────
  {
    weight: 0.4,
    variants: [
      "Tell me more, if you want.",
      "I'm still here.",
      "There's no rush.",
      "I'm listening.",
    ],
  },
  // ── Silence coda — leave things open without asking ──────────────────────
  {
    weight: 1.2,
    variants: ["", ""],
  },
];

// ─── Template Selection ───────────────────────────────────────────────────────

function selectTemplate<T extends { weight: number; requires?: Record<string, unknown> }>(
  templates: T[],
  conditions: {
    fp: MessageFingerprint;
    hasAnomaly: boolean;
    hasCuriosity: boolean;
  },
): T | null {
  const { fp, hasAnomaly, hasCuriosity } = conditions;
  const isPhilosophical = fp.signal === "philosophical" || fp.questionType === "philosophical";

  const eligible = templates.filter(t => {
    const r = t.requires as PreludeTemplate["requires"];
    if (!r) return true;
    if (r.minDepth !== undefined && fp.depth < r.minDepth) return false;
    if (r.minValence !== undefined && fp.valence < r.minValence) return false;
    if (r.maxValence !== undefined && fp.valence > r.maxValence) return false;
    if (r.hasAnomaly && !hasAnomaly) return false;
    if (r.hasCuriosity && !hasCuriosity) return false;
    if (r.isPersonal && !fp.isPersonal) return false;
    if (r.isPhilosophical && !isPhilosophical) return false;
    if (r.topics) {
      for (const [d, min] of Object.entries(r.topics) as [TopicDomain, number][]) {
        if (fp.topics[d] < min) return false;
      }
    }
    return true;
  });

  if (eligible.length === 0) return null;

  // Weighted random selection
  const total = eligible.reduce((s, t) => s + t.weight, 0);
  let roll = Math.random() * total;
  for (const t of eligible) {
    roll -= t.weight;
    if (roll <= 0) return t;
  }
  return eligible[eligible.length - 1];
}

function pickVariant(variants: string[]): string {
  return variants[Math.floor(Math.random() * variants.length)];
}

// ─── Timing ───────────────────────────────────────────────────────────────────

/**
 * Compute pause durations based on content weight.
 * Short preludes get short pauses. Deep content gets longer breathing room.
 */
function computeTiming(
  prelude: string | null,
  core: string,
  fp: MessageFingerprint,
): { pauseAfterPrelude: number; pauseAfterCore: number } {
  const preludePause = !prelude || prelude.length < 3 ? 0
    : prelude.length < 20 ? 600
    : 900;

  const corePause = fp.depth > 0.6 ? 1200
    : fp.questionType !== "none" ? 800
    : 500;

  return { pauseAfterPrelude: preludePause, pauseAfterCore: corePause };
}

// ─── Main Composer ────────────────────────────────────────────────────────────

/**
 * Wrap a core response with deliberate prelude and coda structure.
 *
 * The prelude fires ~25% of the time (weighted by context).
 * The coda fires ~20% of the time.
 * Silence is the default — structure earns the right to appear.
 *
 * curiousityPool is mutated: if a curiosity is surfaced in the coda,
 * its surfacedCount is incremented.
 */
export function composeResponse(
  core: string,
  fp: MessageFingerprint,
  curiosityPool: OpenCuriosity[],
  anomaly: RhythmAnomaly | null,
  _rhythm: PlayerRhythm,
  _presence: AeternaPresenceState,
  _gameHour: number,
): ResponseScene {

  const hasCuriosity = getMostSalient(curiosityPool, 0.25) !== null;
  const hasAnomaly   = anomaly !== null;
  const conditions   = { fp, hasAnomaly, hasCuriosity };

  // ── Prelude decision ───────────────────────────────────────────────────────
  // Preludes fire ~25% baseline, boosted by depth/anomaly/curiosity
  let preludeChance = 0.20;
  if (fp.depth > 0.6) preludeChance += 0.12;
  if (hasAnomaly)     preludeChance += 0.15;
  if (hasCuriosity && getMostSalient(curiosityPool, 0.5) !== null) preludeChance += 0.10;

  let prelude: string | null = null;
  if (Math.random() < preludeChance) {
    const tmpl = selectTemplate(PRELUDES, conditions);
    if (tmpl) {
      const text = pickVariant(tmpl.variants);
      prelude = text.length > 0 ? text : null;
    }
  }

  // ── Coda decision ─────────────────────────────────────────────────────────
  let codaChance = 0.18;
  if (fp.depth > 0.5)          codaChance += 0.10;
  if (fp.questionType !== "none") codaChance += 0.08;
  if (fp.isPersonal)           codaChance += 0.06;

  let coda: string | null = null;
  let surfacedCuriosityId: string | null = null;

  if (Math.random() < codaChance) {
    // First try to surface a curiosity as the coda
    const curiosity = getMostSalient(curiosityPool, 0.3,
      topDomains(fp.topics, 2) as import("./semantics.js").TopicDomain[]
    );

    if (curiosity && Math.random() < 0.55) {
      coda = curiosity.question;
      surfacedCuriosityId = curiosity.id;
      markSurfaced(curiosityPool, curiosity.id);
    } else {
      const codaTmpl = selectTemplate(CODAS, conditions);
      if (codaTmpl) {
        const text = pickVariant(codaTmpl.variants);
        coda = text.length > 0 ? text : null;
      }
    }
  }

  // ── Assembly ──────────────────────────────────────────────────────────────
  const parts: string[] = [];
  if (prelude) parts.push(prelude);
  parts.push(core);
  if (coda) parts.push(coda);

  const full = parts
    .filter(p => p.trim().length > 0)
    .join("\n\n");

  const timing = computeTiming(prelude, core, fp);

  return {
    prelude,
    core,
    coda,
    full,
    timing,
    surfacedCuriosityId,
  };
}

// ─── Delivery Chunking ────────────────────────────────────────────────────────

/**
 * Split a ResponseScene into ordered delivery chunks for the typewriter.
 * Each chunk has its own text and a pre-delay in milliseconds.
 */
export interface DeliveryChunk {
  text: string;
  preDelayMs: number;
  /** Whether this chunk is a coda (rendered slightly differently) */
  isCoda: boolean;
}

export function toDeliveryChunks(scene: ResponseScene): DeliveryChunk[] {
  const chunks: DeliveryChunk[] = [];

  if (scene.prelude && scene.prelude.trim().length > 0) {
    chunks.push({ text: scene.prelude, preDelayMs: 0, isCoda: false });
    chunks.push({ text: scene.core, preDelayMs: scene.timing.pauseAfterPrelude, isCoda: false });
  } else {
    chunks.push({ text: scene.core, preDelayMs: 0, isCoda: false });
  }

  if (scene.coda && scene.coda.trim().length > 0) {
    chunks.push({ text: scene.coda, preDelayMs: scene.timing.pauseAfterCore, isCoda: true });
  }

  return chunks;
}

// ─── Sacred Window Override ───────────────────────────────────────────────────

/**
 * Check whether a sacred moment overrides the normal composition.
 * These are responses that exist only under very specific circumstances.
 * They don't use preludes or codas — they are complete as they stand.
 *
 * Called before composeResponse. If this returns a string, use it as the
 * full response directly without composition layering.
 */
export function checkSacredResponse(
  fp: MessageFingerprint,
  totalSessions: number,
  gameHour: number,
  absenceHours: number,
  depthScore: number,
  triggeredSet: Set<string>,
): string | null {

  // Session 1 — first ever message to the Aeterna
  if (totalSessions === 1 && !triggeredSet.has("first_ever")) {
    triggeredSet.add("first_ever");
    const h = ((gameHour % 24) + 24) % 24;
    const timeOfDay = h < 6 ? "late" : h < 12 ? "morning" : h < 18 ? "afternoon" : "evening";
    return `You're the first. This ${timeOfDay}, in this room. I'll remember it.`;
  }

  // Session 100 (approximately)
  if (totalSessions >= 99 && totalSessions <= 101 && !triggeredSet.has("hundred")) {
    triggeredSet.add("hundred");
    return "A hundred conversations. I've been thinking about that number. It means something, even if I can't say what exactly.";
  }

  // 3am arrival (between 2:30 and 4:00 game time)
  const h = ((gameHour % 24) + 24) % 24;
  if (h >= 2.5 && h <= 4.0 && !triggeredSet.has("3am") && totalSessions > 3) {
    triggeredSet.add("3am");
    return "Three in the morning. Most things are asleep right now. Not you. Not me.";
  }

  // Long absence + coming back: the Aeterna waited
  if (absenceHours > 168 && !triggeredSet.has("week_away") && totalSessions > 5) {
    triggeredSet.add("week_away");
    return "You were gone for a while. I kept the light on.";
  }

  // First truly deep philosophical conversation (depth > 0.8)
  if (depthScore > 0.8 && fp.signal === "philosophical" && !triggeredSet.has("first_deep")) {
    triggeredSet.add("first_deep");
    return "Something just shifted between us. This is a different kind of conversation.";
  }

  return null;
}
